import { useCallback, useEffect, useState } from 'react';
import { qcctRatesApi, type QcctRate } from '../lib/api/qcct-rates';
import { useToast } from '../lib/use-toast';

const GROUPS = [
  { key: 'red', num: '1', title: 'BGĐ', roles: 'TGĐ · GĐ · Chủ tịch HĐQT' },
  { key: 'yellow', num: '2', title: 'Cấp cao', roles: 'KTT · GĐ khối · GĐ dự án lớn' },
  { key: 'blue', num: '3', title: 'Cấp trung', roles: 'Trưởng phòng · Team Lead · Senior' },
  { key: 'grey', num: '4', title: 'NV', roles: 'Chuyên viên · NV · Junior · TTS' },
] as const;

const VUNG_ORDER = ['HN_HCM', 'TinhI', 'TinhKhac', 'Foreign'] as const;
const VUNG_LABEL: Record<string, string> = {
  HN_HCM: 'HN / HCM',
  TinhI: 'Tỉnh loại I',
  TinhKhac: 'Tỉnh + huyện',
  Foreign: 'Nước ngoài',
};

const RULES = [
  { label: 'Điều 6', text: 'Chứng từ > 5.000.000đ bắt buộc chuyển khoản (NĐ 320/2025).' },
  { label: 'Điều 21', text: 'Tạm ứng tối đa 100% dự toán chuyến công tác.' },
  { label: 'Điều 22', text: 'Nộp hồ sơ quyết toán trong 7 ngày làm việc sau khi về.' },
  { label: 'Điều 24', text: 'Duyệt KTT → GĐ. Vượt định mức cần TGĐ/GĐ ký thêm.' },
];

function fmt(v: number) { return v.toLocaleString('vi-VN') + 'đ'; }

export function BusinessTravelSection() {
  const { toast, showToast } = useToast();
  const [rates, setRates] = useState<QcctRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await qcctRatesApi.list();
      setRates(r.items);
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const editRate = async (rate: QcctRate, field: 'lodging' | 'perDiem' | 'travel', label: string) => {
    const v = window.prompt(`Sửa ${label} — Nhóm ${rate.nhom} · ${VUNG_LABEL[rate.vung]}`, String(rate[field]));
    if (!v || Number(v) < 0) return;
    try {
      await qcctRatesApi.patch(rate.id, { [field]: Number(v) });
      showToast('Đã cập nhật');
      load();
    } catch (e) { alert(e instanceof Error ? e.message : String(e)); }
  };

  const getRate = (nhom: number, vung: string) => rates.find((r) => r.nhom === nhom && r.vung === vung);

  return (
    <>
      <div className="ct-hero">
        <div className="ct-hero-icon"><svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="14" x="2" y="7" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg></div>
        <div className="ct-hero-meta">
          <h2>Chính sách công tác phí năm 2026</h2>
          <p>Theo Quy chế công tác phí HITEK · 4 nhóm chức danh × 4 nhóm địa bàn · Áp dụng NĐ 320/2025.</p>
        </div>
        <div className="ct-hero-actions">
          <button type="button" className="pay-btn-outline" onClick={() => showToast('Tải QCCT-2026.pdf — file chưa upload')}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /></svg>Tải QCCT.pdf</button>
          <button type="button" className="pay-btn-primary" onClick={() => showToast('Click từng ô trong bảng để chỉnh trực tiếp')}>Chỉnh sửa định mức</button>
        </div>
      </div>

      {err && <div className="pay-table-error">Lỗi: {err}</div>}

      <div className="ct-card">
        <div className="ct-card-head"><h3>4 nhóm chức danh</h3></div>
        <div className="ct-groups">
          {GROUPS.map((g) => (
            <div key={g.key} className={`ct-group ct-group-${g.key}`}>
              <div className="ct-group-num">Nhóm {g.num}</div>
              <div className="ct-group-title">{g.title}</div>
              <div className="ct-group-roles">{g.roles}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="ct-card">
        <div className="ct-card-head">
          <div>
            <h3>Định mức tiền ở · đồng/đêm</h3>
            <p className="sal-card-sub">Click từng ô để chỉnh</p>
          </div>
          <span className="ct-tag">Phụ lục 02</span>
        </div>
        {loading ? <div className="pay-empty">Đang tải…</div> : (
          <table className="ct-tbl">
            <thead>
              <tr>
                <th>Nhóm</th>
                {VUNG_ORDER.map((v) => <th key={v}>{VUNG_LABEL[v]}</th>)}
              </tr>
            </thead>
            <tbody>
              {[1, 2, 3, 4].map((nhom) => (
                <tr key={nhom}>
                  <td><b>N{nhom}</b></td>
                  {VUNG_ORDER.map((v) => {
                    const r = getRate(nhom, v);
                    if (!r) return <td key={v}>—</td>;
                    return (
                      <td key={v} onClick={() => editRate(r, 'lodging', 'Tiền ở/đêm')} style={{ cursor: 'pointer' }} title="Click để sửa">
                        {fmt(r.lodging)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="ct-mini-grid">
        <div className="ct-mini-card">
          <div className="ct-mini-title">Phụ cấp lưu trú · đ/ngày</div>
          <div className="ct-mini-list">
            {[1, 2, 3, 4].map((nhom) => {
              const r = getRate(nhom, 'HN_HCM');
              return (
                <div key={nhom} className="ct-mini-row" onClick={() => r && editRate(r, 'perDiem', 'Phụ cấp lưu trú')} style={{ cursor: r ? 'pointer' : 'default' }}>
                  <span>N{nhom}</span><b>{r ? fmt(r.perDiem) : '—'}</b>
                </div>
              );
            })}
          </div>
        </div>
        <div className="ct-mini-card">
          <div className="ct-mini-title">Đi lại 2 chiều · đ/chuyến</div>
          <div className="ct-mini-list">
            {VUNG_ORDER.map((v) => {
              const r = getRate(1, v);
              return (
                <div key={v} className="ct-mini-row" onClick={() => r && editRate(r, 'travel', 'Đi lại (khoán)')} style={{ cursor: r ? 'pointer' : 'default' }}>
                  <span>{VUNG_LABEL[v]}</span><b>{r ? fmt(r.travel) : '—'}</b>
                </div>
              );
            })}
          </div>
        </div>
        <div className="ct-mini-card">
          <div className="ct-mini-title">Tiếp khách (ngoài QCCT)</div>
          <div className="ct-mini-list">
            <div className="ct-mini-row"><span>N1</span><b>1.500.000đ</b></div>
            <div className="ct-mini-row"><span>N2</span><b>800.000đ</b></div>
            <div className="ct-mini-row"><span>N3</span><b>500.000đ</b></div>
            <div className="ct-mini-row"><span>N4</span><b>300.000đ</b></div>
          </div>
        </div>
      </div>

      <div className="ct-card">
        <div className="ct-card-head"><h3>Rules áp dụng</h3></div>
        <ul className="ct-rules">
          {RULES.map((r) => (
            <li key={r.label}>
              <span className="ct-rule-label">{r.label}</span>
              <span>{r.text}</span>
            </li>
          ))}
        </ul>
      </div>

      {toast && <div className="coa-toast">{toast}</div>}
    </>
  );
}
